import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';

import type { RichSatellite } from '../data/types';
import { SATELLITE_KIND_LABELS } from '../data/satelliteEnrich';
import { ecefToScene } from '../lib/geo3d';
import { createEarthGlobe, GLOBE_CAMERA_FAR, GLOBE_CAMERA_MAX_DISTANCE, GLOBE_CAMERA_MIN_DISTANCE, loadSatelliteIconAtlas } from './satelliteGlobeAssets';
import { createSatelliteKindLayers } from './satelliteKindLayers';
import { hoverSatelliteAtScreen, pickSatelliteAtScreen } from './satelliteGlobePick';

interface OrbitPoints {
  noradId: string;
  points: Array<[number, number, number]>;
}

interface Props {
  satellites: RichSatellite[];
  selectedNoradId: string | null;
  followSat: boolean;
  focusNoradId: string | null;
  focusSeq: number;
  orbitPoints: OrbitPoints | null;
  onSelect: (noradId: string | null) => void;
}

function isMobileGlobe(): boolean {
  return window.matchMedia('(pointer: coarse)').matches || window.innerWidth < 768;
}

export function SatelliteGlobeScene({
  satellites,
  selectedNoradId,
  followSat,
  focusNoradId,
  focusSeq,
  orbitPoints,
  onSelect,
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const satsRef = useRef(satellites);
  const selectedIdRef = useRef(selectedNoradId);
  const followRef = useRef(followSat);
  const pickRef = useRef(onSelect);
  const focusIdRef = useRef(focusNoradId);
  const focusSeqRef = useRef(focusSeq);
  const orbitLineRef = useRef<THREE.Line | null>(null);
  const rafRef = useRef<number | null>(null);
  const controlsRef = useRef<OrbitControls | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);

  useEffect(() => {
    satsRef.current = satellites;
  }, [satellites]);
  useEffect(() => {
    selectedIdRef.current = selectedNoradId;
  }, [selectedNoradId]);
  useEffect(() => {
    followRef.current = followSat;
  }, [followSat]);
  useEffect(() => {
    pickRef.current = onSelect;
  }, [onSelect]);
  useEffect(() => {
    focusIdRef.current = focusNoradId;
    focusSeqRef.current = focusSeq;
  }, [focusNoradId, focusSeq]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: !isMobileGlobe(),
      alpha: false,
      powerPreference: 'high-performance',
    });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setClearColor(0x020617, 1);

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x020617);

    const camera = new THREE.PerspectiveCamera(45, 1, 0.01, GLOBE_CAMERA_FAR);
    camera.position.set(0, 0, 2.8);
    cameraRef.current = camera;

    const controls = new OrbitControls(camera, canvas);
    controlsRef.current = controls;
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    controls.rotateSpeed = 0.5;
    controls.zoomSpeed = 0.8;
    controls.minDistance = GLOBE_CAMERA_MIN_DISTANCE;
    controls.maxDistance = GLOBE_CAMERA_MAX_DISTANCE;
    controls.enablePan = false;

    const sun = new THREE.DirectionalLight(0xffffff, 1.2);
    sun.position.set(5, 2, 3);
    scene.add(sun);
    scene.add(new THREE.AmbientLight(0x467492, 0.75));

    const earth = createEarthGlobe();
    scene.add(earth.group);

    const starGeo = new THREE.BufferGeometry();
    const starCount = 2000;
    const starPos = new Float32Array(starCount * 3);
    const starColors = new Float32Array(starCount * 3);
    for (let i = 0; i < starCount; i++) {
      const r = 80 * (0.85 + Math.random() * 0.15);
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      starPos[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      starPos[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      starPos[i * 3 + 2] = r * Math.cos(phi);
      const bright = Math.random() > 0.15;
      const v = bright ? 0.6 + Math.random() * 0.4 : 0.5 + Math.random() * 0.2;
      starColors[i * 3] = v;
      starColors[i * 3 + 1] = bright ? v * 0.9 : v * 0.92;
      starColors[i * 3 + 2] = bright ? v * 0.7 : v;
    }
    starGeo.setAttribute('position', new THREE.BufferAttribute(starPos, 3));
    starGeo.setAttribute('color', new THREE.BufferAttribute(starColors, 3));
    scene.add(
      new THREE.Points(
        starGeo,
        new THREE.PointsMaterial({
          size: 0.15,
          vertexColors: true,
          transparent: true,
          opacity: 0.85,
          sizeAttenuation: true,
          depthWrite: false,
        }),
      ),
    );

    let kindLayers: ReturnType<typeof createSatelliteKindLayers> | null = null;
    let iconAtlas: Awaited<ReturnType<typeof loadSatelliteIconAtlas>> | null = null;

    const orbitGeo = new THREE.BufferGeometry();
    const orbitLine = new THREE.Line(
      orbitGeo,
      new THREE.LineBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.55 }),
    );
    orbitLine.visible = false;
    orbitLineRef.current = orbitLine;
    scene.add(orbitLine);

    void loadSatelliteIconAtlas().then((atlas) => {
      iconAtlas = atlas;
      kindLayers = createSatelliteKindLayers(atlas, isMobileGlobe());
      scene.add(kindLayers.group);
    });

    const frameOnSatellite = (noradId: string) => {
      const sat = satsRef.current.find((s) => s.noradId === noradId);
      if (!sat) return;
      const [x, y, z] = ecefToScene(sat.position.ecef);
      const target = new THREE.Vector3(x, y, z);
      controls.target.copy(target);
      const radius = Math.max(Math.hypot(x, y, z), 1.05);
      const dist = Math.min(Math.max(radius * 0.55, 1.8), GLOBE_CAMERA_MAX_DISTANCE * 0.45);
      const offset = camera.position.clone().sub(controls.target);
      if (offset.lengthSq() < 1e-6) offset.set(0, 0, 1);
      offset.normalize().multiplyScalar(dist);
      camera.position.copy(target).add(offset);
      controls.update();
    };

    let lastFocusSeq = focusSeqRef.current;
    const pointerDownRef = { x: 0, y: 0 };

    const canvasRect = () => canvas.getBoundingClientRect();

    const updateScene = () => {
      const list = satsRef.current;
      const selectedId = selectedIdRef.current;
      kindLayers?.update(list, selectedId);

      if (focusSeqRef.current !== lastFocusSeq) {
        lastFocusSeq = focusSeqRef.current;
        const focusId = focusIdRef.current;
        if (focusId) frameOnSatellite(focusId);
      }

      const sel = list.find((s) => s.noradId === selectedId);
      if (sel && followRef.current) {
        const [x, y, z] = ecefToScene(sel.position.ecef);
        controls.target.lerp(new THREE.Vector3(x, y, z), 0.12);
        const offset = camera.position.clone().sub(controls.target);
        const dist = Math.max(Math.sqrt(x * x + y * y + z * z) * 0.35, 1.2);
        if (offset.lengthSq() < 1e-6) offset.set(0, 0, 1);
        offset.normalize().multiplyScalar(Math.min(dist, 12));
        camera.position.copy(controls.target).add(offset);
      }
    };

    const resize = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (!w || !h) return;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };

    const loop = () => {
      rafRef.current = requestAnimationFrame(loop);
      if (document.hidden) return;
      updateScene();
      controls.update();
      renderer.render(scene, camera);
    };

    const onPointerDown = (ev: PointerEvent) => {
      pointerDownRef.x = ev.clientX;
      pointerDownRef.y = ev.clientY;
    };

    const onPointerMove = (ev: PointerEvent) => {
      const rect = canvasRect();
      const viewDistance = camera.position.distanceTo(controls.target);
      const hover = hoverSatelliteAtScreen(
        satsRef.current,
        selectedIdRef.current,
        camera,
        ev.clientX - rect.left,
        ev.clientY - rect.top,
        rect.width,
        rect.height,
        viewDistance,
      );
      canvas.style.cursor = hover ? 'pointer' : 'grab';
    };

    const onPointerUp = (ev: PointerEvent) => {
      const dx = ev.clientX - pointerDownRef.x;
      const dy = ev.clientY - pointerDownRef.y;
      if (dx * dx + dy * dy > 36) return;

      const rect = canvasRect();
      const viewDistance = camera.position.distanceTo(controls.target);
      const id = pickSatelliteAtScreen(
        satsRef.current,
        selectedIdRef.current,
        camera,
        ev.clientX - rect.left,
        ev.clientY - rect.top,
        rect.width,
        rect.height,
        viewDistance,
      );
      if (id) pickRef.current(id);
      else pickRef.current(null);
    };

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', onPointerUp);
    resize();
    loop();

    return () => {
      ro.disconnect();
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', onPointerUp);
      canvas.style.cursor = '';
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      controls.dispose();
      kindLayers?.dispose();
      iconAtlas?.dispose();
      earth.dispose();
      renderer.dispose();
      orbitGeo.dispose();
      starGeo.dispose();
    };
  }, []);

  useEffect(() => {
    const orbitLine = orbitLineRef.current;
    if (!orbitLine || !orbitPoints) {
      if (orbitLine) orbitLine.visible = false;
      return;
    }
    const verts = new Float32Array(orbitPoints.points.length * 3);
    for (let i = 0; i < orbitPoints.points.length; i++) {
      const [x, y, z] = ecefToScene(orbitPoints.points[i]);
      verts[i * 3] = x;
      verts[i * 3 + 1] = y;
      verts[i * 3 + 2] = z;
    }
    orbitLine.geometry.dispose();
    orbitLine.geometry = new THREE.BufferGeometry();
    orbitLine.geometry.setAttribute('position', new THREE.BufferAttribute(verts, 3));
    orbitLine.visible = verts.length >= 6;
  }, [orbitPoints]);

  return <canvas ref={canvasRef} className="satellite-globe-canvas h-full w-full touch-none" />;
}

export { SATELLITE_KIND_LABELS };
